/**
 * Validación Espacial para GeocodingOrchestrator
 * 
 * TD-002.F.2: Funciones TRF-06, validación INE y límites municipales
 * 
 * @module services/geocoding/orchestrator/spatialValidation
 * @version 1.0.0
 */

import type { GeocodingResult } from '../../../types/infrastructure';
import type { OrchestrationResult } from './types';

// Validación INE
import {
  validateINEMatch,
  type INEValidationResult,
} from '../../validation/INEValidator';

// Validación límites municipales
import {
  validateWithinMunicipality,
  type BoundaryValidationResult,
} from '../../validation/MunicipalBoundaryValidator';
import { textoSinValidarContorno } from '../../boundaries/MunicipalBoundaryService';

// Validación espacial con config
import {
  applySpatialValidation,
  VALIDATION_CONFIGS,
} from '../validationStrategy';

// Proyecciones UTM ↔ WGS84
import { fromUtm } from '../../../utils/projections';

// Rangos geográficos
import { isWithinAndalucia } from '../../../constants/geographic';

// MJ-B05b: lookup nombre municipio por INE para detectar bug de INE en CartoCiudad
import { getMunicipioByINE } from '../../../lib/CentroidValidator';
import { normalizeForComparison } from '../../../utils/addressCleaner';

// ============================================================================
// TRF-06: VALIDACIÓN ESPACIAL
// ============================================================================

/**
 * Aplica validación espacial TRF-06 al resultado de geocodificación
 * 
 * AUDITORÍA TRF-06 (2025-12-18):
 * - Validación coords entrada (NaN/Infinity)
 * - isValid=undefined si error (no true por defecto)
 * - Mensaje error incluye coordenadas
 * 
 * @param result - Resultado de orquestación a validar
 * @param municipio - Nombre del municipio esperado
 * @param configLevel - Nivel de validación: 'light' | 'standard' | 'strict'
 * @returns OrchestrationResult con spatialValidation actualizado
 */
export function applyTRF06SpatialValidation(
  result: OrchestrationResult,
  municipio: string,
  configLevel: 'light' | 'standard' | 'strict' = 'standard'
): OrchestrationResult {
  if (!result.geocoding) return result;

  const config = VALIDATION_CONFIGS[configLevel];
  if (!config.validateSpatialBoundary) return result;

  const { x, y } = result.geocoding;

  // Validar coordenadas de entrada
  if (!Number.isFinite(x) || !Number.isFinite(y)) {
    const errorMsg = `TRF-06: Coordenadas inválidas (x=${x}, y=${y})`;
    console.warn('[TRF-06]', errorMsg);
    return {
      ...result,
      errors: [...result.errors, errorMsg],
      spatialValidation: { isValid: undefined, message: errorMsg, wgs84Coords: undefined }
    };
  }

  try {
    const wgs84 = fromUtm(x, y, 25830);
    const toValidate = {
      coordenadas: { lng: wgs84.lng, lat: wgs84.lat },
      confianza: result.geocoding.confidence / 100
    };
    const validated = applySpatialValidation(toValidate, municipio, config);

    return {
      ...result,
      spatialValidation: {
        isValid: validated.spatialValidation?.isValid,
        message: validated.spatialValidation?.message ?? 'Validación no ejecutada',
        wgs84Coords: { lat: wgs84.lat, lng: wgs84.lng }
      },
      geocoding: validated.spatialValidation?.isValid === false
        ? { ...result.geocoding, confidence: Math.min(result.geocoding.confidence, validated.confianza * 100) }
        : result.geocoding
    };
  } catch (error) {
    const errorMsg = `TRF-06: ${error instanceof Error ? error.message : String(error)} (x=${x}, y=${y})`;
    console.warn('[TRF-06] Error en validación espacial:', error);
    return {
      ...result,
      errors: [...result.errors, errorMsg],
      spatialValidation: { isValid: undefined, message: errorMsg, wgs84Coords: undefined }
    };
  }
}

// ============================================================================
// B05: VALIDACIÓN DE RESULTADOS DE GEOCODIFICACIÓN
// ============================================================================

/** Umbral de distancia para degradar score (metros) */
const DISTANCE_THRESHOLD_DEGRADE = 100;

/**
 * Umbral máximo (km) para considerar un INE «adyacente».
 * Coincide con INEValidator.DISTANCE_THRESHOLDS.ADJACENT_MAX_KM = 50.
 * Se define aquí como literal para no depender del módulo mocqueado en tests.
 */
const ADJACENT_MAX_KM = 50;

/** Umbral de distancia para rechazar resultado (metros) */
const DISTANCE_THRESHOLD_REJECT = 500;

/**
 * B05: Valida resultado de geocodificación contra INE y límites municipales.
 * 
 * Detecta errores como Colomera (Granada, 18051) vs Colomers (Girona, 17055)
 * que representan 750 km de error pero CartoCiudad puede devolver con INE correcto.
 * 
 * Validaciones:
 * 1. INE: Si el resultado incluye muniCode, verifica contra expectedINE
 *    - >100 km → rechazar (error crítico)
 *    - ≤50 km + conf≥80 → falso positivo adyacente (–10, caso Ayamonte/Lepe)
 *    - <100 km genérico → degradar severamente (–40)
 * 2. Espacial: Verifica que coordenadas estén dentro del municipio esperado
 * 
 * @param result - Resultado de geocodificación a validar
 * @param expectedINE - Código INE esperado (5 dígitos)
 * @param errors - Array donde registrar errores encontrados
 * @returns Objeto con validez y confianza ajustada
 */
export async function validateGeocodingResult(
  result: GeocodingResult,
  expectedINE: string,
  errors: string[]
): Promise<{ isValid: boolean; confidence: number; reason?: string }> {
  let confidence = result.confidence;

  // ===== 1. VALIDACIÓN INE =====
  if (result.muniCode && expectedINE) {
    const ineValidation: INEValidationResult = validateINEMatch(
      expectedINE,
      result.muniCode
    );

    if (!ineValidation.isValid) {
      const distKm = ineValidation.distanceKm ?? 0;
      const errorMsg = `B05_INE: Discrepancia INE ${expectedINE} vs ${result.muniCode} (${distKm.toFixed(1)} km)`;
      errors.push(errorMsg);

      // Error INE crítico (>100 km) → rechazar, salvo bug de CartoCiudad (MJ-B05b)
      if (distKm > 100) {
        // MJ-B05b: CartoCiudad puede devolver un INE incorrecto pero con el
        // nombre de municipio correcto (p.ej. Priego de Córdoba → INE 14055
        // en lugar de 14054 por desplazamiento en su base de municipios de
        // Córdoba). Si el nombre coincide, es un bug de la fuente externa,
        // no un error de localización: degradar –15 en lugar de rechazar.
        const expectedMuniInfo = getMunicipioByINE(expectedINE);
        const expectedMuniName = expectedMuniInfo?.nombre ?? '';
        const returnedMuniName = result.municipality ?? '';
        const nameMatches =
          expectedMuniName.length > 0 &&
          returnedMuniName.length > 0 &&
          normalizeForComparison(returnedMuniName) === normalizeForComparison(expectedMuniName);

        if (nameMatches) {
          console.warn(
            `[B05] ⚠️ ${errorMsg} → nombre coincide («${returnedMuniName}»), posible bug INE CartoCiudad → degradado –15`
          );
          confidence = Math.max(confidence - 15, 20);
        } else {
          console.warn(`[B05] ❌ ${errorMsg} → RECHAZADO`);
          return { isValid: false, confidence: 0, reason: errorMsg };
        }
      } else {
        // distKm ≤ 100: evaluar falso positivo adyacente o degradación menor
        // Falso positivo: CartoCiudad devuelve INE adyacente para coord correcta
        // Criterio: distKm ≤ ADJACENT_MAX_KM (50) Y confianza original ≥ 80
        // Caso real: Ayamonte (21006) / Lepe (21007) → CartoCiudad retorna 21010 (Isla Cristina)
        // con radius=0.0 m. Las coordenadas son correctas; el INE devuelto es el error.
        const isFalsePositive =
          distKm <= ADJACENT_MAX_KM && result.confidence >= 80;

        if (isFalsePositive) {
          console.warn(`[B05] ⚠️ ${errorMsg} → FALSO POSITIVO (adyacente, conf alta)`);
          // Penalización leve: –10 para reflejar incertidumbre residual
          confidence = Math.max(confidence - 10, 20);
        } else {
          // Error INE menor genuino (<100 km) → degradar severamente
          console.warn(`[B05] ⚠️ ${errorMsg} → degradado`);
          confidence = Math.max(confidence - 40, 20);
        }
      }
    }
  }

  // ===== 2. VALIDACIÓN ESPACIAL (LÍMITES MUNICIPALES) =====
  try {
    const boundaryValidation: BoundaryValidationResult = await validateWithinMunicipality(
      result.x,
      result.y,
      expectedINE,
      { toleranceMeters: 50 }
    );

    if (boundaryValidation.sinLimite) {
      // VALMUNI-b2a (DOP-2 a): sin límite, la fila queda «sin validar». Se apunta
      // en la traza; no cambia `isValid` ni la confianza (no bloquea ni baja la nota).
      errors.push(
        `B05_BOUNDS_SIN_VALIDAR: ${
          textoSinValidarContorno(boundaryValidation.municipio, boundaryValidation.sinLimite)
          ?? boundaryValidation.message
        }`
      );
    } else if (!boundaryValidation.isValid) {
      const distM = boundaryValidation.distanceToBoundary;
      const errorMsg = `B05_BOUNDS: Coordenadas fuera del municipio (${distM.toFixed(0)}m)`;

      if (distM >= DISTANCE_THRESHOLD_REJECT) {
        // Fuera por >500m → rechazar
        errors.push(`${errorMsg} → RECHAZADO`);
        console.warn(`[B05] ❌ ${errorMsg} → RECHAZADO`);
        return { isValid: false, confidence: 0, reason: errorMsg };
      } else if (distM >= DISTANCE_THRESHOLD_DEGRADE) {
        // Fuera por 100-500m → degradar
        const penalty = Math.min(30, Math.floor(distM / 10));
        confidence = Math.max(confidence - penalty, 30);
        errors.push(`${errorMsg} (degradado -${penalty}%)`);
        console.warn(`[B05] ⚠️ ${errorMsg} → confianza ${confidence}%`);
      }
      // <100m con tolerancia de 50m → aceptar sin penalización
    }
  } catch (err) {
    // Si falla validación espacial, registrar pero continuar
    console.warn('[B05] Error en validación espacial:', err);
    errors.push(`B05_BOUNDS_ERROR: ${err}`);
  }

  return { isValid: true, confidence };
}

// ============================================================================
// VALIDACIÓN ANDALUCÍA
// ============================================================================

/**
 * Valida si coordenadas UTM30 están dentro de Andalucía
 * 
 * @param x - Coordenada X (UTM30 ETRS89)
 * @param y - Coordenada Y (UTM30 ETRS89)
 * @returns true si está dentro de los límites de Andalucía
 */
export function isValidAndaluciaCoordinate(x: number, y: number): boolean {
  return isWithinAndalucia(x, y);
}

// Re-export tipos útiles
export type { INEValidationResult, BoundaryValidationResult };
export { VALIDATION_CONFIGS };
